import { useEffect, useState } from "react";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import {
  getProcessesProcedureRequest,
  getRushFeesListRequest,
} from "../../../../../../redux/clients/slice";
import { updateServicesFeesRequest } from "../../../../../../utils/APICalls";
import Loading from "../../../../../../components/loading/loading";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import { getInspectionServicesRequest } from "../../../../../../redux/inspection-firms/slice";
import { useFormik } from "formik";
import * as yup from "yup";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
  clientId: string;
};

interface ServiceItem {
  inspectionType: string;
  turnTimeInDays: string;
  clientFee: string;
  inspectorFee: string;
}

interface RushFeeItem {
  id: string;
  label: string;
  clientFee: string;
  inspectorFee: string;
}

interface FormValues {
  services: ServiceItem[];
  rushFees: RushFeeItem[];
  cancellationFee: string;
  tripFee: string;
  remoteAreaFee: string;
  remoteAreaDefinition: string;
  cancellationFeeChecked: boolean;
  tripFeeChecked: boolean;
  remoteAreaFeeChecked: boolean;
}

export default function EditServicesFees({
  showDialog,
  closeDialog,
  data,
  clientId,
}: props) {
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();

  const { inspectionServices } = useSelector(
    (state: any) => state.inspectionFirms
  );

  const { rushFeesList } = useSelector((state: any) => state.clients);

  const inspectionTypesTotal = inspectionServices?.length || 0;
  const rushFeesTotal = rushFeesList?.length || 0;

  const ValidationSchema = yup.object({
    services: yup.array().of(
      yup.object({
        inspectionType: yup.string().required("This field can't be empty"),
        turnTimeInDays: yup
          .string()
          .required("This field can't be empty")
          .test(
            "positive-number",
            "Turn Time must be greater than 0",
            (value) => {
              if (!value) return false;
              const num = Number(value);
              return !isNaN(num) && num > 0;
            }
          ),
        clientFee: yup
          .string()
          .required("This field can't be empty")
          .test(
            "positive-number",
            "Client Fee must be greater than 0",
            (value) => {
              if (!value) return false;
              const num = Number(value);
              return !isNaN(num) && num > 0;
            }
          ),
        inspectorFee: yup
          .string()
          .required("This field can't be empty")
          .test(
            "positive-number",
            "Inspector Fee must be greater than 0",
            (value) => {
              if (!value) return false;
              const num = Number(value);
              return !isNaN(num) && num > 0;
            }
          ),
      })
    ),
    rushFees: yup.array().of(
      yup.object({
        id: yup.string().required("This field can't be empty"),
        label: yup.string().required("This field can't be empty"),
        clientFee: yup
          .string()
          .required("This field can't be empty")
          .test(
            "positive-number",
            "Client Fee must be greater than 0",
            (value) => {
              if (!value) return false;
              const num = Number(value);
              return !isNaN(num) && num > 0;
            }
          ),
        inspectorFee: yup
          .string()
          .required("This field can't be empty")
          .test(
            "positive-number",
            "Inspector Fee must be greater than 0",
            (value) => {
              if (!value) return false;
              const num = Number(value);
              return !isNaN(num) && num > 0;
            }
          ),
      })
    ),
    cancellationFee: yup
      .string()
      .test(
        "conditional-required",
        "This field can't be empty",
        function (value) {
          const { cancellationFeeChecked } = this.parent;
          if (cancellationFeeChecked) {
            if (!value || value === "") {
              return this.createError({
                message: "This field can't be empty",
              });
            }
            const num = Number(value);
            if (isNaN(num) || num <= 0) {
              return this.createError({
                message: "Cancellation Fee must be greater than 0",
              });
            }
          }
          return true;
        }
      ),
    tripFee: yup
      .string()
      .test(
        "conditional-required",
        "This field can't be empty",
        function (value) {
          const { tripFeeChecked } = this.parent;
          if (tripFeeChecked) {
            if (!value || value === "") {
              return this.createError({
                message: "This field can't be empty",
              });
            }
            const num = Number(value);
            if (isNaN(num) || num <= 0) {
              return this.createError({
                message: "Trip Fee must be greater than 0",
              });
            }
          }
          return true;
        }
      ),
    remoteAreaFee: yup
      .string()
      .test(
        "conditional-required",
        "This field can't be empty",
        function (value) {
          const { remoteAreaFeeChecked } = this.parent;
          if (remoteAreaFeeChecked) {
            if (!value || value === "") {
              return this.createError({
                message: "This field can't be empty",
              });
            }
            const num = Number(value);
            if (isNaN(num) || num <= 0) {
              return this.createError({
                message: "Remote Area Fee must be greater than 0",
              });
            }
          }
          return true;
        }
      ),
    remoteAreaDefinition: yup.string().notRequired(),
    cancellationFeeChecked: yup.boolean().required(),
    tripFeeChecked: yup.boolean().required(),
    remoteAreaFeeChecked: yup.boolean().required(),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      services: data?.services || [],
      rushFees: data?.rushFees || [],
      cancellationFee: data?.fee?.cancellationFee || "",
      tripFee: data?.fee?.tripFee || "",
      remoteAreaFee: data?.fee?.remoteAreaFee || "",
      remoteAreaDefinition: data?.fee?.remoteAreaDefinition || "",
      cancellationFeeChecked: !!data?.fee?.cancellationFee,
      tripFeeChecked: !!data?.fee?.tripFee,
      remoteAreaFeeChecked: !!data?.fee?.remoteAreaFee,
    },
    validationSchema: ValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const isValid = await formik.validateForm();
      if (!isValid) {
        formik.setTouched({
          services: formik.values.services.map(() => ({})),
          rushFees: formik.values.rushFees.map(() => ({})),
          cancellationFee: true,
          tripFee: true,
          remoteAreaFee: true,
          remoteAreaDefinition: true,
          cancellationFeeChecked: true,
          tripFeeChecked: true,
          remoteAreaFeeChecked: true,
        });
        return;
      }
      updateServicesFees(values);
    },
  });

  const handleServiceTypeChange = (e: SelectChangeEvent, index: number) => {
    const allServices = [...formik.values.services];
    allServices[index].inspectionType = e.target.value;
    formik.setFieldValue("services", allServices);
  };

  const handleServiceChange = (index: number, field: string, value: any) => {
    const updatedServices = [...formik.values.services];
    updatedServices[index] = {
      ...updatedServices[index],
      [field]: value,
    };
    formik.setFieldValue("services", updatedServices);
  };

  const getAvailableInspectionTypes = (currentIndex: number) => {
    const selectedTypes = formik.values.services
      .map((service, index) =>
        index !== currentIndex ? service.inspectionType : null
      )
      .filter(Boolean);

    return inspectionServices.filter(
      (service: any) => !selectedTypes.includes(service.inspectionType)
    );
  };

  const getAvailableRushFees = (currentIndex: number) => {
    const selectedRushFeeIds = formik.values.rushFees
      .map((rushFee, index) => (index !== currentIndex ? rushFee.id : null))
      .filter(Boolean);

    return (
      rushFeesList?.filter(
        (rushFee: any) => !selectedRushFeeIds.includes(rushFee.id)
      ) || []
    );
  };

  const addService = () => {
    const newServices = [
      ...formik.values.services,
      {
        inspectionType: "",
        turnTimeInDays: "",
        clientFee: "",
        inspectorFee: "",
      },
    ];
    formik.setFieldValue("services", newServices);
  };

  const removeService = (index: number) => {
    const updatedServices = formik.values.services.filter(
      (_, i) => i !== index
    );
    formik.setFieldValue("services", updatedServices);
  };

  const handleRushFeeChange = (index: number, field: string, value: any) => {
    const updatedRushFees = [...formik.values.rushFees];

    if (field === "id") {
      const selectedRushFee = rushFeesList?.find(
        (fee: any) => fee.id === value
      );
      updatedRushFees[index] = {
        ...updatedRushFees[index],
        id: value,
        label: selectedRushFee?.label || "",
        inspectorFee: selectedRushFee?.inspectorFee || "",
      };
    } else {
      updatedRushFees[index] = {
        ...updatedRushFees[index],
        [field]: value,
      };
    }
        
    formik.setFieldValue("rushFees", updatedRushFees);
  };

  const addRushFee = () => {
    const newRushFees = [
      ...formik.values.rushFees,
      {
        id: "",
        label: "",
        clientFee: "",
        inspectorFee: "",
      },
    ];
    formik.setFieldValue("rushFees", newRushFees);
  };

  const removeRushFee = (index: number) => {
    const updatedRushFees = formik.values.rushFees.filter(
      (_, i) => i !== index
    );
    formik.setFieldValue("rushFees", updatedRushFees);
  };

  const handleMiscFeeChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
    formik.validateField(field);
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    formik.setFieldValue(field, checked);
    if (!checked) {
      const feeField = field.replace("Checked", "");
      if (feeField === "remoteAreaDefinition") {
        formik.setFieldValue(feeField, "");
      } else {
        formik.setFieldValue(feeField, "0");
      }
    }
    const feeField = field.replace("Checked", "");
    formik.validateField(feeField);
  };

  const updateServicesFees = async (values: FormValues) => {
    setShowLoading(true);
    const payload = {
      services: values.services,
      rushFees: values.rushFees?.map((fee) => ({
        rushFeeId: fee.id,
        clientFee: fee.clientFee,
        inspectorFee: fee.inspectorFee,
      })),
      cancellationFee: values.cancellationFee
        ? Number(values.cancellationFee)
        : null,
      tripFee: values.tripFee ? Number(values.tripFee) : null,
      remoteAreaFee:
        values.remoteAreaFeeChecked && values.remoteAreaFee
          ? Number(values.remoteAreaFee)
          : null,
      remoteAreaDefinition: values.remoteAreaFeeChecked
        ? values.remoteAreaDefinition
        : null,
    };
    try {
      const response = await updateServicesFeesRequest(clientId, payload);
      if (response?.status === 204) {
        dispatch(getProcessesProcedureRequest({ id: clientId }));
        closeDialog();
      } else if (response?.status === 400) {
        setShowErrorNotification(true);
        setErrorMessage(response?.data?.detail || "An error occurred");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("An error occurred while updating services and fees");
    } finally {
      setShowLoading(false);
    }
  };

  useEffect(() => {
    dispatch(getInspectionServicesRequest());
    dispatch(getRushFeesListRequest());
  }, []); // eslint-disable-line

  return (
    <>
      <DialogContainer
        title="Client - Processes & Procedures - Schedule of Services & Fees"
        closeDialog={closeDialog}
        showDialog={showDialog}
        onSubmit={formik.handleSubmit}
      >
        <Box py={2} sx={{ display: "flex" }}>
          <Typography className="model-label-txt" sx={{ mb: 2 }}>
            Services
          </Typography>
          <Box sx={{ width: "calc(100% - 192px) !important" }}>
            {formik.values.services.map((service, index) => (
              <>
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    width: "100% !important",
                    mt: index > 0 ? 2 : 0,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{ width: "100% !important" }}
                  >
                    <Box display="flex">
                      <FormControl
                        fullWidth
                        error={
                          !!(formik.errors.services?.[index] as any)
                            ?.inspectionType &&
                          !!(formik.touched.services?.[index] as any)
                            ?.inspectionType
                        }
                      >
                        <InputLabel id="country-label">
                          Inspection Type
                        </InputLabel>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={service.inspectionType || ""}
                          onChange={(e) => handleServiceTypeChange(e, index)}
                          label="Inspection Type"
                        >
                          {getAvailableInspectionTypes(index).map(
                            (option: any) => (
                              <MenuItem
                                key={option.inspectionType}
                                value={option.inspectionType}
                              >
                                {option.displayName}
                              </MenuItem>
                            )
                          )}
                        </Select>
                        {!!(formik.errors.services?.[index] as any)
                          ?.inspectionType &&
                          !!(formik.touched.services?.[index] as any)
                            ?.inspectionType && (
                            <Typography
                              color="error"
                              variant="caption"
                              sx={{ mt: 1 }}
                            >
                              {
                                (formik.errors.services?.[index] as any)
                                  ?.inspectionType
                              }
                            </Typography>
                          )}
                      </FormControl>
                    </Box>

                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Box flex={1}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Contracted Turn Time"
                          inputProps={{
                            min: 0,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          sx={{
                            "& input[type=number]": {
                              "-moz-appearance": "textfield",
                            },
                            "& input[type=number]::-webkit-outer-spin-button": {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                            "& input[type=number]::-webkit-inner-spin-button": {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                          }}
                          value={service.turnTimeInDays || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (
                              value === "" ||
                              (!isNaN(Number(value)) && Number(value) >= 0)
                            ) {
                              handleServiceChange(
                                index,
                                "turnTimeInDays",
                                value
                              );
                            }
                          }}
                          onKeyDown={(e) => {
                            if (
                              e.key === "-" ||
                              e.key === "e" ||
                              e.key === "E" ||
                              e.key === "+"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) =>
                            (e.target as HTMLInputElement).blur()
                          }
                          error={
                            !!(formik.errors.services?.[index] as any)
                              ?.turnTimeInDays &&
                            !!(formik.touched.services?.[index] as any)
                              ?.turnTimeInDays
                          }
                          helperText={
                            (formik.touched.services?.[index] as any)
                              ?.turnTimeInDays &&
                            (formik.errors.services?.[index] as any)
                              ?.turnTimeInDays
                              ? (formik.errors.services?.[index] as any)
                                  ?.turnTimeInDays
                              : ""
                          }
                        />
                      </Box>
                      <Box flex={1}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Contracted Client Fee"
                          inputProps={{
                            min: 0,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          sx={{
                            "& input[type=number]": {
                              "-moz-appearance": "textfield",
                            },
                            "& input[type=number]::-webkit-outer-spin-button": {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                            "& input[type=number]::-webkit-inner-spin-button": {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                          }}
                          value={service.clientFee || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (
                              value === "" ||
                              (!isNaN(Number(value)) && Number(value) >= 0)
                            ) {
                              handleServiceChange(index, "clientFee", value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (
                              e.key === "-" ||
                              e.key === "e" ||
                              e.key === "E" ||
                              e.key === "+"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) =>
                            (e.target as HTMLInputElement).blur()
                          }
                          InputProps={{
                            startAdornment: (
                              <Typography sx={{ mr: 1 }}>$</Typography>
                            ),
                          }}
                          error={
                            !!(formik.errors.services?.[index] as any)
                              ?.clientFee &&
                            !!(formik.touched.services?.[index] as any)
                              ?.clientFee
                          }
                          helperText={
                            (formik.touched.services?.[index] as any)
                              ?.clientFee &&
                            (formik.errors.services?.[index] as any)?.clientFee
                              ? (formik.errors.services?.[index] as any)
                                  ?.clientFee
                              : ""
                          }
                        />
                      </Box>
                      <Box flex={1}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Inspector Fee"
                          inputProps={{
                            min: 0,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          sx={{
                            "& input[type=number]": {
                              "-moz-appearance": "textfield",
                            },
                            "& input[type=number]::-webkit-outer-spin-button": {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                            "& input[type=number]::-webkit-inner-spin-button": {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                          }}
                          value={service.inspectorFee || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (
                              value === "" ||
                              (!isNaN(Number(value)) && Number(value) >= 0)
                            ) {
                              handleServiceChange(index, "inspectorFee", value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (
                              e.key === "-" ||
                              e.key === "e" ||
                              e.key === "E" ||
                              e.key === "+"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) =>
                            (e.target as HTMLInputElement).blur()
                          }
                          InputProps={{
                            startAdornment: (
                              <Typography sx={{ mr: 1 }}>$</Typography>
                            ),
                          }}
                          error={
                            !!(formik.errors.services?.[index] as any)
                              ?.inspectorFee &&
                            !!(formik.touched.services?.[index] as any)
                              ?.inspectorFee
                          }
                          helperText={
                            (formik.touched.services?.[index] as any)
                              ?.inspectorFee &&
                            (formik.errors.services?.[index] as any)
                              ?.inspectorFee
                              ? (formik.errors.services?.[index] as any)
                                  ?.inspectorFee
                              : ""
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                  <IconButton
                    sx={{
                      color: "#D32F2F",
                    }}
                    onClick={() => removeService(index)}
                    size="small"
                  >
                    <DeleteOutlineOutlinedIcon />
                  </IconButton>
                </Box>
                <Divider />
              </>
            ))}
            <Box mt={2}>
              {formik.values.services.length < inspectionTypesTotal && (
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={addService}
                  sx={{ textTransform: "none" }}
                >
                  ADD ADDITIONAL SERVICES
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Rush Fees Section */}
        <Box py={2} sx={{ display: "flex" }}>
          <Typography className="model-label-txt" sx={{ mb: 2 }}>
            Rush Fees
          </Typography>
          <Box sx={{ width: "calc(100% - 192px) !important" }}>
            {formik.values.rushFees.map((rushFee, index) => (
              <>
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    width: "100% !important",
                    mt: index > 0 ? 2 : 0,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{ width: "100% !important" }}
                  >
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Box flex={1}>
                        <FormControl
                          fullWidth
                          error={
                            !!(formik.errors.rushFees?.[index] as any)?.id &&
                            !!(formik.touched.rushFees?.[index] as any)?.id
                          }
                        >
                          <InputLabel id="rush-fee-label">
                            Rush Fee Label
                          </InputLabel>
                          <Select
                            labelId="rush-fee-select-label"
                            id="rush-fee-select"
                            value={rushFee.id || ""}
                            onChange={(e) =>
                              handleRushFeeChange(index, "id", e.target.value)
                            }
                            label="Rush Fee Label"
                          >
                            {getAvailableRushFees(index).map((fee: any) => (
                              <MenuItem key={fee.id} value={fee.id}>
                                {fee.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {!!(formik.errors.rushFees?.[index] as any)?.id &&
                          !!(formik.touched.rushFees?.[index] as any)?.id && (
                            <Typography
                              color="error"
                              variant="caption"
                              sx={{ mt: 1 }}
                            >
                              {(formik.errors.rushFees?.[index] as any)?.id}
                            </Typography>
                          )}
                      </Box>
                      <Box flex={1}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Client Fee"
                          inputProps={{
                            min: 0,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          sx={{
                            "& input[type=number]": {
                              "-moz-appearance": "textfield",
                            },
                            "& input[type=number]::-webkit-outer-spin-button": {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                            "& input[type=number]::-webkit-inner-spin-button": {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                          }}
                          value={rushFee.clientFee || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (
                              value === "" ||
                              (!isNaN(Number(value)) && Number(value) >= 0)
                            ) {
                              handleRushFeeChange(index, "clientFee", value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (
                              e.key === "-" ||
                              e.key === "e" ||
                              e.key === "E" ||
                              e.key === "+"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) =>
                            (e.target as HTMLInputElement).blur()
                          }
                          InputProps={{
                            startAdornment: (
                              <Typography sx={{ mr: 1 }}>$</Typography>
                            ),
                          }}
                          error={
                            !!(formik.errors.rushFees?.[index] as any)
                              ?.clientFee &&
                            !!(formik.touched.rushFees?.[index] as any)
                              ?.clientFee
                          }
                          helperText={
                            (formik.touched.rushFees?.[index] as any)
                              ?.clientFee &&
                            (formik.errors.rushFees?.[index] as any)?.clientFee
                              ? (formik.errors.rushFees?.[index] as any)
                                  ?.clientFee
                              : ""
                          }
                        />
                      </Box>
                      <Box flex={1}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Inspector Fee"
                          inputProps={{
                            min: 0,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          sx={{
                            "& input[type=number]": {
                              "-moz-appearance": "textfield",
                            },
                            "& input[type=number]::-webkit-outer-spin-button": {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                            "& input[type=number]::-webkit-inner-spin-button": {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                          }}
                          value={rushFee.inspectorFee || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (
                              value === "" ||
                              (!isNaN(Number(value)) && Number(value) >= 0)
                            ) {
                              handleRushFeeChange(index, "inspectorFee", value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (
                              e.key === "-" ||
                              e.key === "e" ||
                              e.key === "E" ||
                              e.key === "+"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) =>
                            (e.target as HTMLInputElement).blur()
                          }
                          InputProps={{
                            startAdornment: (
                              <Typography sx={{ mr: 1 }}>$</Typography>
                            ),
                          }}
                          error={
                            !!(formik.errors.rushFees?.[index] as any)
                              ?.inspectorFee &&
                            !!(formik.touched.rushFees?.[index] as any)
                              ?.inspectorFee
                          }
                          helperText={
                            (formik.touched.rushFees?.[index] as any)
                              ?.inspectorFee &&
                            (formik.errors.rushFees?.[index] as any)
                              ?.inspectorFee
                              ? (formik.errors.rushFees?.[index] as any)
                                  ?.inspectorFee
                              : ""
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                  <IconButton
                    sx={{
                      color: "#D32F2F",
                    }}
                    onClick={() => removeRushFee(index)}
                    size="small"
                  >
                    <DeleteOutlineOutlinedIcon />
                  </IconButton>
                </Box>
                <Divider />
              </>
            ))}
            <Box mt={2}>
              {formik.values.rushFees.length < rushFeesTotal && (
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={addRushFee}
                  sx={{ textTransform: "none" }}
                >
                  ADD ADDITIONAL RUSH FEES
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Misc Fees & Surcharges Section */}
        <Box py={2} sx={{ display: "flex" }}>
          <Typography className="model-label-txt" sx={{ mb: 2 }}>
            Misc Fees & Surcharges
          </Typography>
          <Box sx={{ width: "calc(100% - 192px) !important" }}>
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Cancellation Fee */}
              <Box display="flex" alignItems="center" gap={2}>
                <Box flex={1} display="flex" justifyContent="space-between">
                  <FormGroup sx={{ ml: 1, width: 250 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.cancellationFeeChecked}
                          onChange={(e) =>
                            handleCheckboxChange(
                              "cancellationFeeChecked",
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Cancellation Fee"
                    />
                  </FormGroup>
                  <TextField
                    disabled={!formik.values.cancellationFeeChecked}
                    sx={{
                      width: "544px",
                      "& input[type=number]": {
                        "-moz-appearance": "textfield",
                      },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                    }}
                    type="number"
                    label="Cancellation Fee"
                    inputProps={{
                      min: 0,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    value={
                      formik.values.cancellationFeeChecked
                        ? formik.values.cancellationFee || ""
                        : "0"
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (
                        value === "" ||
                        (!isNaN(Number(value)) && Number(value) >= 0)
                      ) {
                        handleMiscFeeChange("cancellationFee", value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" ||
                        e.key === "e" ||
                        e.key === "E" ||
                        e.key === "+"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    }}
                    error={
                      !!formik.errors.cancellationFee &&
                      formik.touched.cancellationFee
                    }
                    helperText={
                      formik.touched.cancellationFee &&
                      formik.errors.cancellationFee
                        ? formik.errors.cancellationFee
                        : ""
                    }
                  />
                </Box>
              </Box>
              <Divider />
              <Box display="flex" alignItems="center" gap={2}>
                <Box flex={1} display="flex" justifyContent="space-between">
                  <FormGroup sx={{ ml: 1, width: 250 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.tripFeeChecked}
                          onChange={(e) =>
                            handleCheckboxChange(
                              "tripFeeChecked",
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Trip Fee"
                    />
                  </FormGroup>
                  <TextField
                    disabled={!formik.values.tripFeeChecked}
                    sx={{
                      width: "544px",
                      "& input[type=number]": {
                        "-moz-appearance": "textfield",
                      },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                    }}
                    type="number"
                    label="Trip Fee"
                    inputProps={{
                      min: 0,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    value={
                      formik.values.tripFeeChecked
                        ? formik.values.tripFee || ""
                        : "0"
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (
                        value === "" ||
                        (!isNaN(Number(value)) && Number(value) >= 0)
                      ) {
                        handleMiscFeeChange("tripFee", value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" ||
                        e.key === "e" ||
                        e.key === "E" ||
                        e.key === "+"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    }}
                    error={!!formik.errors.tripFee && formik.touched.tripFee}
                    helperText={
                      formik.touched.tripFee && formik.errors.tripFee
                        ? formik.errors.tripFee
                        : ""
                    }
                  />
                </Box>
              </Box>
              <Divider />
              <Box display="flex" alignItems="center" gap={2}>
                <Box flex={1} display="flex" justifyContent="space-between">
                  <FormGroup sx={{ ml: 1, width: 250 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.remoteAreaFeeChecked}
                          onChange={(e) =>
                            handleCheckboxChange(
                              "remoteAreaFeeChecked",
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Remote Area Fee"
                    />
                  </FormGroup>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <TextField
                      disabled={!formik.values.remoteAreaFeeChecked}
                      sx={{
                        width: "544px",
                        mb: 2,
                        "& input[type=number]": {
                          "-moz-appearance": "textfield",
                        },
                        "& input[type=number]::-webkit-outer-spin-button": {
                          "-webkit-appearance": "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          "-webkit-appearance": "none",
                          margin: 0,
                        },
                      }}
                      fullWidth
                      type="number"
                      label="Remote Area Fee"
                      inputProps={{
                        min: 0,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      value={
                        formik.values.remoteAreaFeeChecked
                          ? formik.values.remoteAreaFee || ""
                          : "0"
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          value === "" ||
                          (!isNaN(Number(value)) && Number(value) >= 0)
                        ) {
                          handleMiscFeeChange("remoteAreaFee", value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.key === "-" ||
                          e.key === "e" ||
                          e.key === "E" ||
                          e.key === "+"
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 1 }}>$</Typography>
                        ),
                      }}
                      error={
                        !!formik.errors.remoteAreaFee &&
                        formik.touched.remoteAreaFee
                      }
                      helperText={
                        formik.touched.remoteAreaFee &&
                        formik.errors.remoteAreaFee
                          ? formik.errors.remoteAreaFee
                          : ""
                      }
                    />
                    <TextField
                      disabled={!formik.values.remoteAreaFeeChecked}
                      sx={{ width: "544px" }}
                      multiline
                      rows={3}
                      size="small"
                      label="Remote Area Definition"
                      value={
                        formik.values.remoteAreaFeeChecked
                          ? formik.values.remoteAreaDefinition || ""
                          : ""
                      }
                      onChange={(e) =>
                        handleMiscFeeChange(
                          "remoteAreaDefinition",
                          e.target.value
                        )
                      }
                      placeholder=""
                      error={
                        !!formik.errors.remoteAreaDefinition &&
                        formik.touched.remoteAreaDefinition
                      }
                      helperText={
                        formik.touched.remoteAreaDefinition &&
                        formik.errors.remoteAreaDefinition
                          ? formik.errors.remoteAreaDefinition
                          : ""
                      }
                    />
                  </Box>
                </Box>
              </Box>
              <Box></Box>
            </Box>
          </Box>
        </Box>
      </DialogContainer>
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      {showLoading && <Loading />}
    </>
  );
}
